import React, { useState } from 'react';

import { FormattedMessage } from 'react-intl';
import {
  Tooltip,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';

import { RouterOutput, trpc } from '../../api/trpc';
import { Trash } from '../../atoms/icons/trash';
import { ScheduleLabel } from './schedule.label';
import { DialogClose } from '../../atoms/dialog-close';

type Item = RouterOutput['schedules']['list'][number];

type Props = {
  schedule: Item;
};

export const ScheduleRemove = ({ schedule }: Props) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const utils = trpc.useContext();
  const { mutate: remove } = trpc.schedules.remove.useMutation({
    onSuccess: () => {
      utils.schedules.list.invalidate();
      setDialogOpen(false);
    },
    onError() {
      alert('Chyba');
    },
  });

  // Set on every occurrence of a series, whether or not it has materialized yet.
  const { parentId } = schedule;

  const handleRemoveOccurrence = () => {
    if (!parentId) return;
    remove({ id: parentId, occurrenceAt: schedule.startAt });
  };

  const handleRemoveSeries = () => {
    if (!parentId) return;
    remove({ id: parentId });
  };

  const handleRemove = () => {
    remove({ id: schedule.id });
  };

  return (
    <>
      <Tooltip title="Smazat">
        <IconButton onClick={() => setDialogOpen(true)} size="small">
          <Trash sx={{ fontSize: '0.875rem' }} />
        </IconButton>
      </Tooltip>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogClose onClose={() => setDialogOpen(false)} />
        <DialogTitle sx={{ textAlign: 'center', mb: 1 }} typography="h3">
          <Trash sx={{ color: 'primary.main', fontSize: '1.5rem' }} />
          <br />
          {parentId ? (
            <FormattedMessage
              id="schedules.remove.recurring.title"
              defaultMessage="Smazat opakující se plán"
            />
          ) : (
            <FormattedMessage
              id="schedules.remove.confirm"
              defaultMessage="Opravdu chcete plán smazat?"
            />
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center' }}>
            <ScheduleLabel schedule={schedule} />
          </Box>
        </DialogContent>

        {parentId ? (
          <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 1 }}>
            <Button
              onClick={handleRemoveOccurrence}
              variant="contained"
              fullWidth
            >
              <FormattedMessage
                id="schedules.remove.recurring.instance"
                defaultMessage="Smazat pouze tento výskyt"
              />
            </Button>
            <Button
              onClick={handleRemoveSeries}
              variant="outlined"
              fullWidth
              sx={{ ml: '0 !important' }}
            >
              <FormattedMessage
                id="schedules.remove.recurring.series"
                defaultMessage="Smazat celou sérii"
              />
            </Button>
            <Button
              onClick={() => setDialogOpen(false)}
              variant="outlined"
              fullWidth
              sx={{ ml: '0 !important' }}
            >
              <FormattedMessage
                id="schedules.remove.no"
                defaultMessage="Zrušit"
              />
            </Button>
          </DialogActions>
        ) : (
          <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'row' }}>
            <Button
              onClick={() => setDialogOpen(false)}
              variant="outlined"
              fullWidth
              sx={{ ml: '0 !important' }}
            >
              <FormattedMessage
                id="schedules.remove.no"
                defaultMessage="Zrušit"
              />
            </Button>
            <Button onClick={handleRemove} variant="contained" fullWidth>
              <FormattedMessage
                id="schedules.remove.yes"
                defaultMessage="Smazat"
              />
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};
